import React, { useState, useEffect, useRef } from "react";
import { MapService } from "../../services/mapService";
import { Position } from "../../types";
import { getTrackingService } from "../../services/trackingService";
import { TrackingData } from "../../services/trackingProvider";
import { DeviceTrackingProvider } from "../../services/deviceTrackingProvider";
import { settingsService } from "../../services/settingsService";
import debugx from "debugx";

const debug = debugx("bikedash:compass");

type MapFollowMode =
  | "none"
  | "follow"
  | "follow-north"
  | "compass"
  | "compass-locked";

interface CompassRoseProps {
  mapService: MapService | null;
  currentPosition?: Position;
}

export function CompassRose({ mapService, currentPosition }: CompassRoseProps) {
  const [mode, setMode] = useState<MapFollowMode>("none");
  const [mapRotation, setMapRotation] = useState(0);
  const [deviceHeading, setDeviceHeading] = useState<number | null>(null);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [hasTrackingError, setHasTrackingError] = useState(false);
  const [hasTransientError, setHasTransientError] = useState(false);
  const lastModeBeforeDrag = useRef<MapFollowMode>("none");
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const isLongPress = useRef(false);
  const transientErrorTimer = useRef<NodeJS.Timeout | null>(null);

  const getUpdateInterval = () => {
    const maxUpdateRate =
      settingsService.getSettings().user.maxUpdateRate || 30;
    return 1000 / maxUpdateRate; // Convert FPS to milliseconds
  };

  // Subscribe to tracking service for unified position and compass data
  useEffect(() => {
    const trackingService = getTrackingService();

    const subscription = trackingService.subscribe((data: TrackingData) => {
      setTrackingData(data);

      // Clear errors if we have valid GPS position
      if (data.position && data.position.lat && data.position.lng) {
        setHasTrackingError(false);
        setHasTransientError(false);
        // Clear any pending transient error timer
        if (transientErrorTimer.current) {
          clearTimeout(transientErrorTimer.current);
          transientErrorTimer.current = null;
        }
      }

      // Extract compass heading from the tracking data
      let compassHeading: number | null = null;

      if (
        data.source === "device" &&
        data.metadata?.compassHeading !== undefined
      ) {
        compassHeading = data.metadata.compassHeading;
      } else if (
        data.source === "simulation" &&
        data.position?.compassHeading !== undefined
      ) {
        compassHeading = data.position.compassHeading;
      } else if (data.position?.heading !== undefined) {
        // Fallback to GPS heading if no compass heading available
        compassHeading = data.position.heading;
      }

      setDeviceHeading(compassHeading);

      // Just track heading for display - don't push updates
      if (compassHeading !== null) {
        debug("CompassRose heading: %s°", compassHeading.toFixed(1));
      }
    });

    // Subscribe to tracking errors
    const errorSubscription = trackingService.onError((error) => {
      if (error) {
        setHasTrackingError(true);
        debug("CompassRose: Tracking error:", error.message);
      }
      // Note: Don't clear error here - only clear when we get valid GPS
    });

    // Subscribe to transient errors (runtime GPS glitches)
    const transientErrorSubscription = trackingService.onTransientError(() => {
      debug(
        "CompassRose: Transient GPS error - will stay red until valid position",
      );
      setHasTransientError(true);
      // Don't set a timer - will clear when we get valid GPS position
    });

    return () => {
      subscription.unsubscribe();
      errorSubscription.unsubscribe();
      transientErrorSubscription.unsubscribe();
      if (transientErrorTimer.current) {
        clearTimeout(transientErrorTimer.current);
      }
    };
  }, []);

  // Update map rotation when heading changes in compass modes
  useEffect(() => {
    if (
      deviceHeading !== null &&
      (mode === "compass" || mode === "compass-locked")
    ) {
      setMapRotation(-deviceHeading);
    }
  }, [deviceHeading, mode]);

  // The MapService now handles all mode changes internally,
  // including when the user manually interacts with the map.
  // We just listen to mode changes via onModeChange.

  // Listen for mode changes from map service - this is our source of truth
  useEffect(() => {
    if (!mapService) return;

    const handleModeChange = (newMode: string) => {
      debug("Map service changed mode to: %s", newMode);
      // Update our internal state to match
      if (newMode === "follow") {
        setMode("follow");
      } else if (newMode === "follow-north") {
        setMode("follow-north");
        setMapRotation(0); // Visual feedback only
      } else if (newMode === "follow-heading") {
        setMode("compass");
        // Track the rotation for visual feedback
        if (deviceHeading !== null) {
          setMapRotation(-deviceHeading);
        }
      } else if (newMode === "free") {
        setMode("none");
      }
    };

    // Subscribe to mode changes from MapService
    const unsubscribe = mapService.onModeChange(handleModeChange);
    return unsubscribe;
  }, [mapService, deviceHeading]);

  // GPS status based on tracking service data
  const [hasGPS, setHasGPS] = useState(false);
  useEffect(() => {
    const newHasGPS = !!(
      trackingData?.isActive && trackingData?.position !== null
    );
    if (newHasGPS !== hasGPS) {
      setHasGPS(newHasGPS);
    }
  }, [trackingData, hasGPS]);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!mapService || isLongPress.current) {
      // Reset after a delay to ensure this check happens first
      setTimeout(() => {
        isLongPress.current = false;
      }, 100);
      return;
    }

    // If there's a tracking error, try to restart tracking
    if (hasTrackingError) {
      debug("Attempting to restart tracking after error");
      const trackingService = getTrackingService();
      // Clear error immediately to show we're trying
      setHasTrackingError(false);
      try {
        trackingService.clearError();
        await trackingService.start("auto");
        debug("Tracking restarted successfully");
      } catch (error) {
        debug.error("Failed to restart tracking:", error);
        // Re-set error if restart fails
        setHasTrackingError(true);
      }
      return;
    }

    // Cycle through modes via mapService - it will notify us back
    switch (mode) {
      case "none":
        // First click: follow with free rotation
        mapService.setMode("follow");
        break;
      case "follow":
        // Second click: follow with compass heading
        mapService.setMode("follow-heading");
        break;
      case "compass":
        // Third click: follow with north lock
        mapService.setMode("follow-north");
        break;
      case "follow-north":
        // Fourth click: back to compass heading
        mapService.setMode("follow-heading");
        break;
      case "compass-locked":
        // Alternative compass mode - go to north
        mapService.setMode("follow-north");
        break;
    }
  };

  const handleLongPress = () => {
    if (!mapService) return;

    // Long press behavior depends on current mode
    switch (mode) {
      case "compass":
      case "compass-locked":
        // In heading mode - switch to north
        mapService.setMode("follow-north");
        break;
      case "follow-north":
        // In north mode - switch to heading
        mapService.setMode("follow-heading");
        break;
      case "none":
      case "follow":
        // In free mode - toggle between north and heading without changing mode
        const currentRotation = mapService.getRotation();

        // If currently at north (or close to it), rotate to heading
        if (Math.abs(currentRotation) < 0.1) {
          if (deviceHeading !== null) {
            // Rotate to GPS heading (convert degrees to radians)
            mapService.setRotation((-deviceHeading * Math.PI) / 180);
          }
        } else {
          // If rotated, go back to north
          mapService.setRotation(0);
        }
        break;
    }
  };

  const handlePressStart = (e: React.MouseEvent | React.TouchEvent) => {
    isLongPress.current = false;

    // Clear any existing timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
    }

    longPressTimer.current = setTimeout(() => {
      isLongPress.current = true;
      handleLongPress();
      longPressTimer.current = null;
    }, 500);
  };

  const handlePressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  const getButtonContent = () => {
    if (mode === "none") {
      // Gray dot when no GPS, blue dot when GPS available but not following
      return <div className={`compass-dot ${hasGPS ? "" : "no-gps"}`} />;
    } else if (mode === "compass" || mode === "compass-locked") {
      // Arrow pointing up for compass mode
      return (
        <svg
          className="compass-arrow"
          viewBox="0 0 24 24"
          width="24"
          height="24"
        >
          <path d="M12 2L8 8h3v12h2V8h3z" fill="currentColor" />
        </svg>
      );
    } else if (mode === "follow-north") {
      // N with arrow for north up
      return (
        <div className="compass-north">
          <svg
            className="compass-arrow-small"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          >
            <path d="M12 2L8 8h8z" fill="currentColor" />
          </svg>
          <span>N</span>
        </div>
      );
    } else {
      // Follow mode - filled blue dot
      return <div className="compass-dot active" />;
    }
  };

  const getTooltip = () => {
    if (hasTrackingError) {
      return "GPS error - tap to retry";
    }
    switch (mode) {
      case "follow":
        return "Following location";
      case "follow-north":
        return "Following with north up";
      case "compass":
      case "compass-locked":
        return "Compass orientation";
      default:
        return "Center on location";
    }
  };

  return (
    <button
      className={`compass-rose ${mode !== "none" ? "active" : ""} ${hasTrackingError || hasTransientError ? "error" : ""} mode-${mode}`}
      onClick={handleClick}
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressEnd}
      title={getTooltip()}
      style={{
        transform:
          mode === "compass" || mode === "compass-locked"
            ? `rotate(${mapRotation}deg)`
            : undefined,
      }}
    >
      {getButtonContent()}
    </button>
  );
}
